<script lang="ts">
  import {
    type CalendarEvent,
    type CalendarDay,
    type LaneAssignable,
    CalendarCellInteraction,
    getSingleDayEvents,
    getSpanTypeClass,
    assignLanes,
    maxLaneCount,
    MAX_VISIBLE_EVENTS,
    MONTH_NAMES,
    WEEKDAY_LABELS,
    eventColor,
    eventEndDate,
    eventTotalDays,
    isMultiDayEvent,
    getTodayStr,
    parseCalendarDate,
    MS_PER_DAY,
    formatEventTimeShort,
    hoveredDateStr,
    getWeekDates,
    cellClickMode,
    openEventModalForEdit,
  } from '../lib/calendarViewHelpers.svelte';
  import { onMount, tick } from 'svelte';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    year: number;
    events: CalendarEvent[];
    eventsByDate: Map<string, CalendarEvent[]>;
  }
  let { year, events, eventsByDate }: Props = $props();
  const ci = new CalendarCellInteraction(() => events);

  const SPAN_ROW_HEIGHT = 16;
  const DAY_NUMBER_HEIGHT = 16;

  interface DayCell extends CalendarDay {
    date: Date;
    month: number;
    isOutsideYear: boolean;
  }
  interface WeekRow {
    days: DayCell[];
    monthLabel: string | null;
    weekIndex: number;
  }
  interface SpanningEvent extends LaneAssignable {
    event: CalendarEvent;
    continuesLeft: boolean;
    continuesRight: boolean;
  }

  // ── Data computation ───────────────────────────────────────────────────────

  function computeWeeks(yr: number, ebd: Map<string, CalendarEvent[]>): WeekRow[] {
    const todayStr = getTodayStr();

    const jan1 = new Date(yr, 0, 1);
    const start = new Date(jan1);
    start.setDate(start.getDate() - start.getDay());

    const dec31 = new Date(yr, 11, 31);
    const end = new Date(dec31);
    if (end.getDay() < 6) {
      end.setDate(end.getDate() + (6 - end.getDay()));
    }

    const weeks: WeekRow[] = [];
    let weekIndex = 0;
    let lastMonthLabel = -1;

    const weekStart = new Date(start);
    while (true) {
      if (weekStart > end) break;
      const cursor = new Date(weekStart);
      const days: DayCell[] = [];
      let monthLabel: string | null = null;

      for (let d = 0; d < 7; d++) {
        const dateStr = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
        const weekday = cursor.getDay();
        const month = cursor.getMonth();
        const isOutsideYear = cursor.getFullYear() !== yr;

        if (!isOutsideYear && month !== lastMonthLabel) {
          monthLabel = MONTH_NAMES[month];
          lastMonthLabel = month;
        }

        days.push({
          date: new Date(cursor),
          dateStr,
          day: cursor.getDate(),
          weekday,
          month,
          isToday: dateStr === todayStr,
          isWeekend: weekday === 0 || weekday === 6,
          isOutsideYear,
          events: isOutsideYear ? [] : ebd.get(dateStr) || [],
        });

        cursor.setDate(cursor.getDate() + 1);
      }

      weeks.push({ days, monthLabel, weekIndex });
      weekStart.setDate(weekStart.getDate() + 7);
      weekIndex++;
    }

    return weeks;
  }

  let weeks = $derived(computeWeeks(year, eventsByDate));

  function getSpanningEvents(week: WeekRow): SpanningEvent[] {
    const weekStartStr = week.days[0].dateStr;
    const weekEndStr = week.days[6].dateStr;

    const spans: SpanningEvent[] = events
      .filter((e) => isMultiDayEvent(e) && e.date <= weekEndStr && eventEndDate(e) >= weekStartStr)
      .map((e) => {
        const eStart = parseCalendarDate(e.date);
        const eEnd = parseCalendarDate(eventEndDate(e));
        const wStart = parseCalendarDate(weekStartStr);

        const startCol = Math.max(
          0,
          Math.round((eStart.getTime() - wStart.getTime()) / MS_PER_DAY),
        );
        const endCol = Math.min(6, Math.round((eEnd.getTime() - wStart.getTime()) / MS_PER_DAY));

        return {
          event: e,
          startCol,
          endCol,
          lane: 0,
          continuesLeft: eStart < wStart,
          continuesRight: eEnd > parseCalendarDate(weekEndStr),
        };
      });

    return assignLanes(spans);
  }

  // ── Week highlight & scroll to today ───────────────────────────────────────

  let weekDates = $derived($hoveredDateStr ? getWeekDates($hoveredDateStr) : new Set<string>());

  let scrollContainer: HTMLDivElement | undefined = $state();

  onMount(async () => {
    await tick();
    const todayEl = scrollContainer?.querySelector('.today');
    if (todayEl) {
      todayEl.scrollIntoView({ block: 'center', behavior: 'instant' });
    }
  });
</script>

<div class="wall-container" bind:this={scrollContainer}>
  <div class="wall-header">
    <div class="month-col-header"></div>
    {#each WEEKDAY_LABELS as wh, i}
      <div class="weekday-header" class:weekend-header={i === 0 || i === 6}>{wh}</div>
    {/each}
  </div>

  <div class="wall-body">
    {#each weeks as week (week.weekIndex)}
      {@const spanningEvents = getSpanningEvents(week)}
      {@const spanRows = maxLaneCount(spanningEvents)}
      {@const spanPadding = spanRows * SPAN_ROW_HEIGHT}
      <div class="week-row">
        <div class="month-col">
          {#if week.monthLabel}
            <span class="month-label">{week.monthLabel}</span>
          {/if}
        </div>
        <div class="days-container">
          {#if spanningEvents.length > 0}
            <div
              class="spanning-layer"
              style="top: {DAY_NUMBER_HEIGHT}px; height: {spanPadding}px;"
            >
              {#each spanningEvents as span (span.event.id)}
                <button
                  type="button"
                  class="spanning-bar {getSpanTypeClass(span.event.type)}"
                  class:continues-left={span.continuesLeft}
                  class:continues-right={span.continuesRight}
                  class:dragging={ci.draggingEventId === span.event.id}
                  draggable={!$viewport.isMobile}
                  ondragstart={(e) => ci.pillDragStart(e, span.event)}
                  ondragend={ci.pillDragEnd}
                  onclick={() => ci.spanClick(span.event)}
                  style="
                    top: {span.lane * SPAN_ROW_HEIGHT}px;
                    left: calc({span.startCol} / 7 * 100%);
                    width: calc({span.endCol - span.startCol + 1} / 7 * 100%);
                  "
                  title="{span.event.title} ({eventTotalDays(span.event)} days)"
                >
                  <span class="spanning-title">{span.event.title}</span>
                  <span class="spanning-days">{eventTotalDays(span.event)}d</span>
                </button>
              {/each}
            </div>
          {/if}
          {#each week.days as day, colIdx (day.dateStr)}
            {@const singleDayEvents = getSingleDayEvents(day.events)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="day-cell"
              class:today={day.isToday}
              class:weekend={day.isWeekend}
              class:outside={day.isOutsideYear}
              class:first-of-month={day.day === 1 && !day.isOutsideYear}
              class:drop-target={ci.dropTargetDate === day.dateStr}
              class:week-highlight={weekDates.has(day.dateStr)}
              class:hovered-cell={$hoveredDateStr === day.dateStr}
              onclick={() => {
                if (!day.isOutsideYear && $cellClickMode === 'single') ci.cellClick(day.dateStr);
              }}
              ondblclick={() => {
                if (!day.isOutsideYear && $cellClickMode === 'double') ci.cellClick(day.dateStr);
              }}
              onmouseenter={(e) => {
                if (!day.isOutsideYear)
                  ci.cellEnter(
                    e,
                    day.dateStr,
                    `${WEEKDAY_LABELS[day.weekday]} ${MONTH_NAMES[day.month]} ${day.day}`,
                    day.events,
                  );
              }}
              onmouseleave={ci.cellLeave}
              ondragover={(e) => {
                if (!day.isOutsideYear) ci.cellDragOver(e, day.dateStr);
              }}
              ondragleave={ci.cellDragLeave}
              ondrop={(e) => {
                if (!day.isOutsideYear) ci.cellDrop(e, day.dateStr);
              }}
            >
              <span class="day-number" class:first-of-month={day.day === 1 && !day.isOutsideYear}>
                {day.day}
              </span>
              {#if spanRows > 0}
                <div class="span-spacer" style="height: {spanPadding}px;"></div>
              {/if}
              {#if singleDayEvents.length > 0}
                <div class="event-pills">
                  {#each singleDayEvents.slice(0, MAX_VISIBLE_EVENTS) as event (event.id)}
                    {@const timeStr = formatEventTimeShort(event)}
                    <button
                      class="event-pill"
                      style="border-left-color: {eventColor(event.type)}"
                      class:dragging={ci.draggingEventId === event.id}
                      draggable={!$viewport.isMobile}
                      ondragstart={(e) => ci.pillDragStart(e, event)}
                      ondragend={ci.pillDragEnd}
                      onclick={(e) => {
                        e.stopPropagation();
                        openEventModalForEdit(event);
                      }}
                    >
                      {#if timeStr}<span class="event-time">{timeStr}</span>{/if}{event.title}
                    </button>
                  {/each}
                  {#if singleDayEvents.length > MAX_VISIBLE_EVENTS}
                    <span class="event-more"
                      >+{singleDayEvents.length - MAX_VISIBLE_EVENTS} more</span
                    >
                  {/if}
                </div>
              {/if}
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</div>

<style>
  @import '../lib/calendarCellStyles.css';

  .wall-container {
    width: calc(100% - 1rem);
    height: calc(100% - 1rem);
    margin: 0.5rem;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    background: #e5e7eb;
    box-sizing: border-box;
  }

  .wall-header {
    display: grid;
    grid-template-columns: 2.5rem repeat(7, 1fr);
    gap: 1px;
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .month-col-header {
    background: #f3f4f6;
    min-height: 1.5rem;
  }

  .weekday-header {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    font-size: 0.6875rem;
    font-weight: 600;
    color: #6b7280;
    padding: 0.25rem 0;
    min-height: 1.5rem;
  }

  .weekday-header.weekend-header {
    background: #e5e7eb;
    color: #9ca3af;
  }

  .wall-body {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  .week-row {
    display: grid;
    grid-template-columns: 2.5rem 1fr;
    gap: 1px;
    min-height: 5.5rem;
  }

  .month-col {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
    padding: 0.125rem;
    overflow: hidden;
  }

  .month-label {
    font-size: 0.5625rem;
    font-weight: 800;
    color: #374151;
    letter-spacing: 0.05em;
    writing-mode: vertical-lr;
    transform: rotate(180deg);
    white-space: nowrap;
  }

  .days-container {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    background: #e5e7eb;
    position: relative;
  }

  /* Wall-specific: larger cells */
  .day-cell {
    padding: 0.25rem 0.3125rem;
    min-height: 5.5rem;
  }

  .day-cell.first-of-month {
    background: #fefce8;
  }
  .day-cell.first-of-month:hover {
    background: #fef3c7;
  }
  .day-cell.first-of-month.today {
    background: #eff6ff;
    box-shadow: inset 0 0 0 2px #3b82f6;
  }
  .day-cell.first-of-month.week-highlight {
    background: #fef9e0;
  }

  .day-cell.outside {
    cursor: default;
    background: #f3f4f6;
    color: #d1d5db;
  }
  .day-cell.outside:hover {
    background: #f3f4f6;
  }

  .day-number {
    font-size: 0.6875rem;
    font-weight: 600;
    color: #374151;
    line-height: 1;
    margin-bottom: 0.1875rem;
  }

  .day-cell.outside .day-number {
    color: #d1d5db;
  }

  .day-number.first-of-month {
    color: #b45309;
    font-weight: 800;
  }

  /* Wall-specific: larger pills and spanning bars */
  .event-pills {
    gap: 2px;
  }
  .event-pill {
    padding: 0.0625rem 0.25rem;
    font-size: 0.5625rem;
    border-radius: 0 2px 2px 0;
  }
  .event-more {
    font-size: 0.5rem;
  }

  .spanning-bar {
    height: 14px;
    padding: 0 4px;
    border-radius: 3px;
    font-size: 0.5625rem;
    gap: 0.25rem;
  }
  .spanning-bar.continues-left {
    padding-left: 2px;
  }
  .spanning-bar.continues-right {
    padding-right: 2px;
  }

  .spanning-days {
    flex-shrink: 0;
    font-size: 0.5rem;
    opacity: 0.7;
  }
</style>
